import { useModal } from '../../../providers/ModalProvider';
import Modal from '../Modal';
import { useWorkspace } from '../../../providers/WorkspaceProvider';
import { modals } from '../../../providers/modals';
import styles from './CreateTemplateModal.module.scss';
import TypeSelectorDropdown from '../../../components/dropdowns/TypeSelectorDropdown';
import { Text } from '../../typography';
import { Form, ProgressBar } from 'react-bootstrap';
import { useCurrentUser } from '../../../providers/UserProvider';
import { loader } from 'graphql.macro';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import {
  getRoute,
  paths,
  specialTemplateTypes,
} from '../../../constants/strings';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keys } from '../../../utilities/translator/translation_keys';
import Button from '../../Button';
import { handleJsonImport } from './CreateTemplateModal.util';

const createTemplateMutation = loader('./CreateTemplateModal.graphql');
const categoryMutation = loader('../TemplateCsvModal.category.graphql');
const questionMutation = loader('../TemplateCsvModal.question.graphql');
const logicMutation = loader('../TemplateCsvModal.logic.graphql');
const actionMutation = loader('../TemplateCsvModal.action.graphql');

export default function CreateTemplateModal() {
  const { updateModal, closeModal, modalState } = useModal();
  const { assessmentTypes } = useCurrentUser();
  const navigate = useNavigate();
  const [createTemplate] = useMutation(createTemplateMutation);
  const [addCategory] = useMutation(categoryMutation);
  const [addQuestion] = useMutation(questionMutation);
  const [addConditionalLogic] = useMutation(logicMutation);
  const [addConditionalAction] = useMutation(actionMutation);
  const { workspaceId } = useWorkspace();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);

  const {
    show,
    modalTitle,
    type,
    types,
    title,
    description,
    reviewRequired,
    analysisRequested,
    allWorkspaces,
  } = modalState.createTemplateModal;

  const getTypeList = () => {
    const list = types?.length ? types : Object.values(assessmentTypes);
    return list
      ?.filter((t) => t.active)
      ?.map((t, index) => ({
        title: t.title || t.name,
        icon: t.icon,
        color: t.color,
        key: t.key,
        id: index,
      }));
  };
  const typeList = getTypeList();

  const update = (variables) =>
    updateModal({
      modalName: modals.createTemplate,
      variables: {
        ...variables,
      },
    });
  const onHide = () => {
    setImporting(false);
    setImportProgress(0);
    closeModal({ modalName: modals.createTemplate });
  };
  const template = useMemo(() => {
    const allWorkspaceCheck =
      specialTemplateTypes.includes(type.key) || allWorkspaces;
    return {
      title,
      description,
      templateType: type.key,
      reviewRequired,
      analysisRequested,
      workspaceId: allWorkspaceCheck ? null : workspaceId,
    };
  }, [
    allWorkspaces,
    analysisRequested,
    description,
    reviewRequired,
    title,
    type.key,
    workspaceId,
  ]);

  const onJsonImport = (e) =>
    handleJsonImport(e, {
      createTemplate,
      addCategory,
      addQuestion,
      addConditionalLogic,
      addConditionalAction,
      setImporting,
      setImportProgress,
      workspaceId,
      onHide,
      navigate,
      t,
    });

  return (
    <Modal
      open={show || false}
      title={
        modalTitle ||
        t(keys.action.CREATE_VARIABLE, {
          variable: t(keys.common.TEMPLATE),
        })
      }
      onClose={onHide}
      submitText={t(keys.action.CREATE)}
      submitDisabled={!title || importing}
      hideCancel
      onSubmit={() =>
        createTemplate({
          variables: template,
        }).then(
          ({
            data: {
              addInspectionTemplate: { id },
            },
          }) => {
            navigate(getRoute(workspaceId, paths.templates, id));
          },
        )
      }
    >
      <div className={styles.container}>
        <TypeSelectorDropdown
          onChange={(newType) => {
            update({ type: newType });
          }}
          selected={type}
          items={typeList}
          type={t(keys.common.TYPE)}
        />
        <div className={styles.content}>
          <Text weight="semiBold" size="md" noMargin>
            {t(keys.common.TITLE)}
          </Text>
          <Form.Control
            type="text"
            placeholder={t(keys.templates.CREATE_MODAL_TITLE_PLACEHOLDER)}
            value={title}
            onChange={(e) => update({ title: e.target.value })}
          />
          <Text weight="semiBold" size="md" noMargin>
            {`${t(keys.common.DESCRIPTION)} (${t(keys.common.OPTIONAL)})`}
          </Text>
          <Form.Control
            as="textarea"
            rows={5}
            placeholder={t(keys.templates.DESCRIPTION_PLACEHOLDER)}
            value={description}
            onChange={(e) => update({ description: e.target.value })}
          />
        </div>
      </div>
      <br />
      <div className={styles.jsonImport}>
        <Text noMargin weight="semiBold" size="sm">
          {t(keys.templates.JSON_IMPORT_LABEL)}
        </Text>
        <Text noMargin size="sm" color="secondary">
          {t(keys.templates.JSON_IMPORT_DESCRIPTION)}
        </Text>
        {importing ? (
          <ProgressBar
            animated
            now={importProgress}
            className={styles.progressBar}
          />
        ) : (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              style={{ display: 'none' }}
              onChange={onJsonImport}
            />
            <Button
              value={t(keys.templates.JSON_IMPORT_BUTTON)}
              variant="secondary"
              outlined
              onClick={() => fileInputRef.current?.click()}
            />
          </>
        )}
      </div>
    </Modal>
  );
}
